import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/store/auth.store'

export function LandingPage() {
  const { t } = useTranslation()
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const currentYear = new Date().getFullYear()

  return (
    <div className="min-h-screen bg-surface text-on-surface">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-surface/80 backdrop-blur-xl shadow-sm flex justify-between items-center w-full px-4 md:px-10 py-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
            <span className="text-white font-bold text-sm">M</span>
          </div>
          <span className="font-display text-lg font-bold text-primary">{t('app.name')}</span>
        </div>
        <nav className="hidden md:flex gap-8 items-center">
          <a href="#features" className="text-sm font-medium text-on-surface-variant hover:text-primary transition-colors">{t('landing.nav.features')}</a>
          <a href="#testimonials" className="text-sm font-medium text-on-surface-variant hover:text-primary transition-colors">{t('landing.nav.testimonials')}</a>
        </nav>
        <div className="flex items-center gap-3">
          {isAuthenticated ? (
            <Link to="/dashboard" className="px-5 py-2 bg-primary text-white rounded-full text-sm font-semibold hover:opacity-90 transition-opacity">
              {t('landing.nav.dashboard')}
            </Link>
          ) : (
            <>
              <Link to="/auth/login" className="text-sm font-medium text-primary hover:underline">
                {t('landing.nav.login')}
              </Link>
              <Link to="/auth/register" className="px-5 py-2 bg-primary text-white rounded-full text-sm font-semibold hover:opacity-90 transition-opacity">
                {t('landing.nav.start')}
              </Link>
            </>
          )}
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden pt-12 md:pt-24 pb-16 md:pb-32 px-4 md:px-10">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
          {/* Left: Text */}
          <div className="z-10 text-center md:text-left">
            <span className="inline-block px-4 py-1.5 mb-6 rounded-full bg-primary/10 text-primary text-xs font-semibold uppercase tracking-wider">
              {t('landing.hero.badge')}
            </span>
            <h1 className="font-display text-4xl md:text-6xl font-bold text-on-surface mb-6 leading-tight">
              {t('landing.hero.title_start')}
              <span className="text-primary">{t('landing.hero.title_highlight')}</span>
            </h1>
            <p className="text-base md:text-lg text-on-surface-variant mb-10 max-w-lg mx-auto md:mx-0">
              {t('landing.hero.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
              <Link
                to="/auth/register"
                className="px-8 py-4 bg-primary text-white rounded-xl font-semibold shadow-[0px_2px_10px_rgba(0,103,125,0.3)] hover:opacity-90 active:scale-95 transition-all text-center"
              >
                {t('landing.hero.cta_primary')}
              </Link>
              <Link
                to="/auth/login"
                className="px-8 py-4 border-2 border-primary text-primary rounded-xl font-semibold hover:bg-primary/5 active:scale-95 transition-all text-center"
              >
                {t('landing.hero.cta_secondary')}
              </Link>
            </div>
          </div>

          {/* Right: Phone mockup with chat bubbles */}
          <div className="relative flex justify-center items-center">
            <div className="absolute w-[120%] h-[120%] bg-gradient-to-tr from-primary/10 via-secondary/5 to-transparent rounded-full blur-3xl -z-10" />
            <div className="w-full max-w-[360px] aspect-[4/5] relative rounded-[2rem] overflow-hidden shadow-2xl border-4 border-white bg-surface-container-low">
              <div className="absolute inset-0 p-4 flex flex-col gap-3 pt-8">
                <div className="max-w-[80%] bg-white/90 backdrop-blur-sm rounded-2xl rounded-tl-none p-3 shadow-sm border border-white/50">
                  <p className="text-sm text-on-surface">{t('landing.hero.mock_question')}</p>
                </div>
                <div className="max-w-[80%] self-end bg-primary rounded-2xl rounded-tr-none p-3 shadow-sm">
                  <p className="text-sm text-white">{t('landing.hero.mock_answer')}</p>
                </div>
                <div className="max-w-[85%] bg-white/90 backdrop-blur-sm rounded-2xl rounded-tl-none p-3 shadow-sm border border-white/50">
                  <p className="text-sm text-on-surface">{t('landing.hero.mock_reply')}</p>
                </div>
              </div>
              <div className="absolute bottom-4 left-3 right-3 bg-white/80 backdrop-blur-sm rounded-full px-4 py-2.5 flex items-center justify-between shadow-lg border border-white/50">
                <span className="text-sm text-on-surface-variant">{t('landing.hero.mock_input')}</span>
                <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                  <span className="material-symbols-outlined text-white text-sm">mic</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Bento Grid */}
      <section className="py-24 px-4 md:px-10 bg-surface-container-low" id="features">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-display text-3xl md:text-5xl font-semibold text-on-surface mb-4">
              {t('landing.features.title')}
            </h2>
            <p className="text-lg text-on-surface-variant">
              {t('landing.features.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {/* Feature 1: Large (8/12) */}
            <div className="md:col-span-8 bg-white p-8 md:p-10 rounded-[2.5rem] shadow-sm flex flex-col justify-between group">
              <div>
                <div className="w-14 h-14 bg-primary/15 rounded-2xl flex items-center justify-center mb-8">
                  <span className="material-symbols-outlined text-primary text-3xl">chat_bubble</span>
                </div>
                <h3 className="font-display text-2xl font-semibold text-on-surface mb-4">
                  {t('landing.features.conversation_title')}
                </h3>
                <p className="text-base text-on-surface-variant max-w-md">
                  {t('landing.features.conversation_text')}
                </p>
              </div>
              <div className="mt-8 space-y-3 transform transition-transform group-hover:scale-[1.02] duration-500">
                <div className="flex gap-2 items-end">
                  <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                    <span className="material-symbols-outlined text-primary text-xs">smart_toy</span>
                  </div>
                  <div className="bg-surface-container-low rounded-2xl rounded-tl-none p-3 max-w-xs">
                    <p className="text-sm text-on-surface">{t('landing.features.conversation_example_question')}</p>
                  </div>
                </div>
                <div className="flex gap-2 items-end flex-row-reverse">
                  <div className="w-7 h-7 rounded-full bg-primary flex items-center justify-center shrink-0">
                    <span className="text-white text-xs font-bold">M</span>
                  </div>
                  <div className="bg-primary rounded-2xl rounded-tr-none p-3 max-w-xs">
                    <p className="text-sm text-white">{t('landing.features.conversation_example_answer')}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Feature 2: Gaming (4/12) */}
            <div className="md:col-span-4 bg-secondary p-8 md:p-10 rounded-[2.5rem] shadow-lg flex flex-col text-white relative overflow-hidden">
              <div className="z-10">
                <div className="w-14 h-14 bg-white/20 backdrop-blur-md rounded-2xl flex items-center justify-center mb-8">
                  <span className="material-symbols-outlined text-white text-3xl">workspace_premium</span>
                </div>
                <h3 className="font-display text-2xl font-semibold mb-4">{t('landing.features.gaming_title')}</h3>
                <p className="text-base opacity-90">
                  {t('landing.features.gaming_text')}
                </p>
              </div>
              <div className="mt-auto z-10 pt-8">
                <div className="h-3 w-full bg-white/20 rounded-full overflow-hidden mb-2">
                  <div className="h-full w-3/4 bg-white rounded-full" />
                </div>
                <span className="text-xs font-semibold opacity-90">{t('landing.features.gaming_level')}</span>
              </div>
              <div className="absolute -right-20 -bottom-20 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
            </div>

            {/* Feature 3: Full width (12/12) */}
            <div className="md:col-span-12 bg-surface-container-high p-8 md:p-10 rounded-[2.5rem] shadow-sm flex flex-col md:flex-row items-center gap-12">
              <div className="flex-1">
                <div className="w-14 h-14 bg-primary/15 rounded-2xl flex items-center justify-center mb-8">
                  <span className="material-symbols-outlined text-primary text-3xl">psychology</span>
                </div>
                <h3 className="font-display text-2xl font-semibold text-on-surface mb-4">{t('landing.features.adaptive_title')}</h3>
                <p className="text-base text-on-surface-variant">
                  {t('landing.features.adaptive_text')}
                </p>
              </div>
              <div className="flex-1 grid grid-cols-2 gap-4 w-full">
                <div className="bg-white p-6 rounded-2xl shadow-sm border-b-4 border-primary/20">
                  <div className="text-primary font-display text-3xl font-bold mb-2">98%</div>
                  <div className="text-sm font-medium text-on-surface-variant">{t('landing.features.stat_retention')}</div>
                </div>
                <div className="bg-white p-6 rounded-2xl shadow-sm border-b-4 border-secondary/20">
                  <div className="text-secondary font-display text-3xl font-bold mb-2">2.5x</div>
                  <div className="text-sm font-medium text-on-surface-variant">{t('landing.features.stat_speed')}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 px-4 md:px-10 bg-surface" id="testimonials">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-display text-3xl md:text-5xl font-semibold text-on-surface mb-4">
              {t('landing.testimonials.title')}
            </h2>
            <p className="text-lg text-on-surface-variant">{t('landing.testimonials.subtitle')}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white p-8 rounded-[2rem] shadow-sm border border-outline-variant flex flex-col gap-4">
              <div className="flex gap-0.5 text-tertiary text-lg">
                <span>★</span><span>★</span><span>★</span><span>★</span><span>★</span>
              </div>
              <p className="text-base text-on-surface-variant flex-1">
                {t('landing.testimonials.first_quote')}
              </p>
              <div className="flex items-center gap-3 pt-2">
                <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-white font-bold shrink-0">
                  S
                </div>
                <div>
                  <div className="text-sm font-semibold text-on-surface">Sophie L.</div>
                  <div className="text-xs text-on-surface-variant">{t('landing.testimonials.first_role')}</div>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-[2rem] shadow-sm border border-outline-variant flex flex-col gap-4">
              <div className="flex gap-0.5 text-tertiary text-lg">
                <span>★</span><span>★</span><span>★</span><span>★</span><span>★</span>
              </div>
              <p className="text-base text-on-surface-variant flex-1">
                {t('landing.testimonials.second_quote')}
              </p>
              <div className="flex items-center gap-3 pt-2">
                <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center text-white font-bold shrink-0">
                  M
                </div>
                <div>
                  <div className="text-sm font-semibold text-on-surface">Marc D.</div>
                  <div className="text-xs text-on-surface-variant">{t('landing.testimonials.second_role')}</div>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-[2rem] shadow-sm border border-outline-variant flex flex-col gap-4">
              <div className="flex gap-0.5 text-tertiary text-lg">
                <span>★</span><span>★</span><span>★</span><span>★</span><span>★</span>
              </div>
              <p className="text-base text-on-surface-variant flex-1">
                {t('landing.testimonials.third_quote')}
              </p>
              <div className="flex items-center gap-3 pt-2">
                <div className="w-10 h-10 rounded-full bg-primary/70 flex items-center justify-center text-white font-bold shrink-0">
                  E
                </div>
                <div>
                  <div className="text-sm font-semibold text-on-surface">Elena K.</div>
                  <div className="text-xs text-on-surface-variant">{t('landing.testimonials.third_role')}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 md:px-10 bg-inverse-surface text-white text-center">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-display text-4xl font-bold mb-8 text-white">
            {t('landing.cta.title')}
          </h2>
          <p className="text-lg opacity-80 mb-12">
            {t('landing.cta.text')}
          </p>
          <Link
            to="/auth/register"
            className="inline-block px-10 py-5 bg-primary text-white rounded-full font-semibold text-lg shadow-xl hover:scale-105 active:scale-95 transition-all"
          >
            {t('landing.cta.button')}
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-inverse-surface border-t border-white/10 pt-16 pb-8 px-4 md:px-10">
        <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-10 mb-12">
          <div className="md:col-span-1">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                <span className="text-white font-bold text-sm">M</span>
              </div>
              <span className="font-display text-lg font-bold text-white">{t('app.name')}</span>
            </div>
            <p className="text-sm text-white/80 leading-relaxed">
              {t('landing.footer.tagline')}
            </p>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-white mb-4 uppercase tracking-wider">{t('landing.footer.product')}</h3>
            <ul className="space-y-3">
              <li><a href="#" className="text-sm text-white/80 hover:text-white transition-colors">{t('landing.footer.features')}</a></li>
              <li><a href="#" className="text-sm text-white/80 hover:text-white transition-colors">{t('landing.footer.pricing')}</a></li>
              <li><a href="#" className="text-sm text-white/80 hover:text-white transition-colors">{t('landing.footer.testimonials')}</a></li>
              <li><a href="#" className="text-sm text-white/80 hover:text-white transition-colors">{t('landing.footer.changelog')}</a></li>
            </ul>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-white mb-4 uppercase tracking-wider">{t('landing.footer.company')}</h3>
            <ul className="space-y-3">
              <li><a href="#" className="text-sm text-white/80 hover:text-white transition-colors">{t('landing.footer.about')}</a></li>
              <li><a href="#" className="text-sm text-white/80 hover:text-white transition-colors">{t('landing.footer.blog')}</a></li>
              <li><a href="#" className="text-sm text-white/80 hover:text-white transition-colors">{t('landing.footer.careers')}</a></li>
              <li><a href="#" className="text-sm text-white/80 hover:text-white transition-colors">{t('landing.footer.contact')}</a></li>
            </ul>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-white mb-4 uppercase tracking-wider">{t('landing.footer.social')}</h3>
            <ul className="space-y-3">
              <li><a href="#" className="text-sm text-white/80 hover:text-white transition-colors">Twitter / X</a></li>
              <li><a href="#" className="text-sm text-white/80 hover:text-white transition-colors">LinkedIn</a></li>
              <li><a href="#" className="text-sm text-white/80 hover:text-white transition-colors">Instagram</a></li>
              <li><a href="#" className="text-sm text-white/80 hover:text-white transition-colors">TikTok</a></li>
            </ul>
          </div>
        </div>

        <div className="max-w-7xl mx-auto border-t border-white/10 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-white/70">{t('landing.footer.rights', { year: currentYear })}</p>
          <div className="flex gap-6">
            <a href="#" className="text-xs text-white/70 hover:text-white transition-colors">{t('landing.footer.privacy')}</a>
            <a href="#" className="text-xs text-white/70 hover:text-white transition-colors">{t('landing.footer.terms')}</a>
          </div>
        </div>
      </footer>
    </div>
  )
}
